import { Box, Button, Chip, Divider, Paper, Popover, Tooltip, Typography } from '@mui/material';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import SmartToyIcon from '@mui/icons-material/SmartToy';

import { IncidentSummary } from '../../api/incidentLog';
import { AgentSessionSummary } from '../../api/agentSessions';
import { extraKinds, formatWhen, incidentTitle } from '../../utils/incidentRekindle';
import { realtimeKindLabel } from '../../utils/realtimeKindLabels';
import IncidentStatusChip from './IncidentStatusChip';
import IncidentLinkChips from './IncidentLinkChips';
import { AgentStatusChip, MonitorButton, TEAL, kindIcon } from './incidentBits';

/**
 * What you get when you click an incident on the map: status, the one-line
 * headline, links to the incidents it is related to, who was notified, what
 * the agent thinks — and a "More" button for the full detail modal.
 */
export default function IncidentPopover({
    projectId,
    incident,
    agentSession,
    agentEnabled,
    anchorPosition,
    onClose,
    onZoom,
    onSelectIncident,
    onOpenDetail,
    onRequestClose,
}: {
    projectId: number;
    incident: IncidentSummary | null;
    agentSession: AgentSessionSummary | undefined;
    agentEnabled: boolean;
    anchorPosition: { top: number; left: number } | null;
    onClose: () => void;
    onZoom: () => void;
    onSelectIncident: (incidentId: number) => void;
    onOpenDetail: () => void;
    onRequestClose: () => void;
}) {
    const open = Boolean(incident && anchorPosition);
    if (!incident) return null;
    const isOpen = incident.closed_at === null;
    const agentActive = agentSession?.status === 'active';
    const summary = agentSession?.latest_summary;

    return (
        <Popover
            open={open}
            anchorReference="anchorPosition"
            anchorPosition={anchorPosition || undefined}
            onClose={onClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
            <Paper sx={{ p: 2, width: 360, maxWidth: '90vw', bgcolor: '#212b37', color: 'white' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                    <Tooltip
                        title={(incident.kinds ?? [incident.kind]).map(realtimeKindLabel).join(' + ')}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
                            {kindIcon(incident.kind, 22)}
                            {extraKinds(incident).map((k) => (
                                <Box key={k} sx={{ display: 'flex', opacity: 0.75 }}>
                                    {kindIcon(k, 16)}
                                </Box>
                            ))}
                        </Box>
                    </Tooltip>
                    <Typography
                        component="span"
                        sx={{ color: 'rgba(255,255,255,0.55)', fontFamily: 'monospace', flexShrink: 0 }}
                    >
                        #{incident.id}
                    </Typography>
                    <Typography sx={{ fontWeight: 700, minWidth: 0 }} noWrap>
                        {incidentTitle(incident)}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1, flexWrap: 'wrap' }}>
                    <IncidentStatusChip status={incident.status} />
                    {agentSession && <AgentStatusChip session={agentSession} />}
                </Box>
                <Typography sx={{ fontSize: '0.82rem', color: '#e2e8f0', mb: 1 }}>
                    {incident.headline}
                </Typography>
                <Box sx={{ mb: 1 }}>
                    <IncidentLinkChips
                        incident={incident}
                        projectId={projectId}
                        onSelectIncident={onSelectIncident}
                    />
                </Box>
                <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8' }}>
                    {incident.detections} detection{incident.detections === 1 ? '' : 's'} · first{' '}
                    {formatWhen(incident.first_detected_at)} · last {formatWhen(incident.last_detected_at)}
                </Typography>
                <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8' }}>
                    {incident.users.length > 0
                        ? `Notified: ${incident.users.join(', ')}`
                        : 'Nobody notified'}
                </Typography>

                {(agentSession || agentEnabled) && (
                    <>
                        <Divider sx={{ my: 1.25, borderColor: 'rgba(255,255,255,0.12)' }} />
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                            <SmartToyIcon sx={{ fontSize: 18, color: agentActive ? TEAL : '#94a3b8', mt: 0.25 }} />
                            <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                                {agentSession ? (
                                    <>
                                        <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8' }}>
                                            {agentActive
                                                ? `Agent monitoring · ${agentSession.wake_count} wake${
                                                      agentSession.wake_count === 1 ? '' : 's'
                                                  } · $${agentSession.spend_usd.toFixed(2)}`
                                                : `Agent ${agentSession.status}${
                                                      agentSession.close_reason
                                                          ? `: ${agentSession.close_reason}`
                                                          : ''
                                                  }`}
                                        </Typography>
                                        {summary && (
                                            <Typography
                                                sx={{
                                                    fontSize: '0.8rem',
                                                    color: '#e2e8f0',
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 4,
                                                    WebkitBoxOrient: 'vertical',
                                                    overflow: 'hidden',
                                                }}
                                            >
                                                {summary.body || summary.title}
                                            </Typography>
                                        )}
                                    </>
                                ) : (
                                    <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                                        No agent on this incident.
                                    </Typography>
                                )}
                                {!agentActive && agentEnabled && isOpen && (
                                    <Box sx={{ mt: 0.75 }}>
                                        <MonitorButton
                                            projectId={projectId}
                                            incident={incident}
                                            onStarted={onOpenDetail}
                                            label="Monitor with AI"
                                        />
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    </>
                )}

                <Divider sx={{ my: 1.25, borderColor: 'rgba(255,255,255,0.12)' }} />
                <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Chip
                        size="small"
                        clickable
                        icon={<CenterFocusStrongIcon />}
                        label="Zoom to"
                        onClick={onZoom}
                        variant="outlined"
                        sx={{ color: '#cbd5e1', borderColor: 'rgba(255,255,255,0.25)', '& .MuiChip-icon': { color: '#cbd5e1' } }}
                    />
                    {isOpen && (
                        <Chip
                            size="small"
                            clickable
                            icon={<TaskAltIcon />}
                            label="Close incident"
                            onClick={onRequestClose}
                            variant="outlined"
                            sx={{ color: '#cbd5e1', borderColor: 'rgba(255,255,255,0.25)', '& .MuiChip-icon': { color: '#cbd5e1' } }}
                        />
                    )}
                    <Box sx={{ flexGrow: 1 }} />
                    <Button
                        size="small"
                        variant="contained"
                        color="secondary"
                        startIcon={<OpenInFullIcon />}
                        onClick={onOpenDetail}
                    >
                        More
                    </Button>
                </Box>
            </Paper>
        </Popover>
    );
}
